import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faReceipt,
  faMoneyBillWave,
  faTag,
  faCalendarAlt,
  faFileAlt,
  faEdit,
  faArrowUp,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";

const TransactionDetails = ({
  isOpen,
  onClose,
  transaction,
  onEdit,
  formatCurrency,
  formatDate,
  getCategoryColor,
}) => {
  if (!transaction) return null;

  // Check if transaction is editable (only Expense and Receive)
  const isEditable =
    transaction.category === "Expense" || transaction.category === "Receive";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Financial Records"
      maxWidth="sm:max-w-2xl"
      footer={
        <div className="flex flex-col sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none transition-all duration-200"
          >
            Close
          </Button>
          {isEditable && (
            <Button
              onClick={onEdit}
              className="gap-2 px-6 bg-blue-800 hover:bg-blue-900 text-white transition-all duration-200"
            >
              <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
              Edit records
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Info Banner */}
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-md border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faReceipt}
              className="text-blue-800 h-4 w-4"
            />
          </div>
          <div>
            <p className="text-sm text-gray-900 font-semibold">
              Financial Records
            </p>
            <p className="text-sm text-gray-600 font-medium">
              Complete transaction details
            </p>
          </div>
        </div>

        {/* Status/Type Banner */}
        <div
          className={`rounded-sm p-4 border ${
            transaction.type === "IN"
              ? "bg-white border-green-200"
              : "bg-white border-red-200"
          }`}
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon
                icon={transaction.type === "IN" ? faArrowUp : faArrowDown}
                className={`h-5 w-5 ${
                  transaction.type === "IN" ? "text-green-600" : "text-red-600"
                }`}
              />
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Transaction Type:{" "}
                  <span
                    className={
                      transaction.type === "IN"
                        ? "text-green-600"
                        : "text-red-600"
                    }
                  >
                    {transaction.type === "IN" ? "Money IN" : "Money OUT"}
                  </span>
                </p>
                <p className="text-sm text-gray-600 font-medium">
                  {transaction.type === "IN"
                    ? "Money coming into the business"
                    : "Money going out of the business"}
                </p>
              </div>
            </div>
            <Badge
              variant="outline"
              className={`${
                transaction.type === "IN"
                  ? "bg-green-600 text-white border-green-200"
                  : "bg-red-600 text-white border-red-400"
              } text-sm font-medium px-3 py-1`}
            >
              {transaction.type}
            </Badge>
          </div>
        </div>

        {/* Transaction Information Grid - Date and Amount on one row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Date */}
          <div className="flex items-start gap-3 p-3 rounded-md border bg-blue-50 border-blue-200">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="h-3.5 w-3.5 text-blue-600"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm uppercase tracking-wider text-gray-600 font-semibold">
                Date
              </p>
              <p className="text-sm font-medium text-gray-900">
                {formatDate(transaction.date)}
              </p>
            </div>
          </div>

          {/* Amount */}
          <div className="flex items-start gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100">
              <FontAwesomeIcon
                icon={faMoneyBillWave}
                className="h-3.5 w-3.5 text-blue-600"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm uppercase tracking-wider text-gray-600 font-semibold">
                Amount
              </p>
              <p
                className={`text-sm font-bold ${
                  transaction.type === "IN" ? "text-green-600" : "text-red-600"
                }`}
              >
                {transaction.type === "IN" ? "+" : "-"}
                {formatCurrency(transaction.amount)}
              </p>
            </div>
          </div>
        </div>

        {/* Description - Full width with textarea style */}
        <div className="flex items-start gap-3 p-3 rounded-lg border bg-gray-50 border-gray-100">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-gray-200">
            <FontAwesomeIcon
              icon={faFileAlt}
              className="h-3.5 w-3.5 text-gray-600"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm uppercase tracking-wider text-gray-600 font-semibold">
              Description
            </p>
            <div className="mt-1 p-2 bg-white rounded-md border border-gray-200 text-sm text-gray-900 min-h-[60px] whitespace-pre-wrap">
              {transaction.description || "-"}
            </div>
          </div>
        </div>

        {/* Category - Full width with Auto-generated badge */}
        <div className="flex items-start gap-3 p-3 rounded-lg border bg-gray-50 border-gray-100">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-gray-200">
            <FontAwesomeIcon
              icon={faTag}
              className="h-3.5 w-3.5 text-gray-600"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm uppercase tracking-wider text-gray-600 font-semibold">
              Category
            </p>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <Badge
                variant="outline"
                className={`${getCategoryColor(transaction.category)} text-sm font-normal px-3 py-1.5 rounded-full`}
              >
                {transaction.category}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TransactionDetails;
